import React, { useEffect, useState } from 'react';
import { CheckCircle2, X } from 'lucide-react';

import { Button } from './ui/button';
import type { SuccessNotificationAction } from '../lib/notify';

export const SUCCESS_TOAST_EVENT = 'rh-tools:success-toast';

type ToastDetail = { message: string; details?: string; action?: SuccessNotificationAction };

export function SuccessToastHost() {
  const [toast, setToast] = useState<ToastDetail | null>(null);

  useEffect(() => {
    const handleToast = (event: Event) => setToast((event as CustomEvent<ToastDetail>).detail);
    window.addEventListener(SUCCESS_TOAST_EVENT, handleToast);
    return () => window.removeEventListener(SUCCESS_TOAST_EVENT, handleToast);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  if (!toast) return null;
  return (
    <div className="fixed bottom-20 left-1/2 z-[100] flex w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 items-start gap-3 rounded-lg border border-success/30 bg-background p-3 shadow-lg" role="status" aria-live="polite">
      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{toast.message}</p>
        {toast.details ? <p className="text-xs text-muted-foreground">{toast.details}</p> : null}
      </div>
      {toast.action ? (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const action = toast.action;
            setToast(null);
            if (action) void action.callback();
          }}
        >
          {toast.action.label}
        </Button>
      ) : null}
      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setToast(null)} aria-label="Dismiss notification"><X className="h-4 w-4" /></Button>
    </div>
  );
}
